import {
  Bot,
  CalendarDays,
  ChartColumn,
  Filter,
  Inbox,
  type LucideIcon,
  Megaphone,
  Plug,
  Settings,
  SquareKanban,
  Sun,
  Users,
  Zap,
} from "lucide-react";

import type { NavigationModule } from "@/lib/api/types";

/**
 * El menú del rediseño: seis grupos por intención («qué vengo a hacer»), no por
 * tipo de pantalla.
 *
 * El agrupado vive acá y no en el backend porque es presentación. Lo que decide
 * el servidor sigue decidiéndolo él: **qué módulos ve este usuario y en qué
 * estado** (`GET /me/navigation`). Un ítem cuyo módulo no llegó no se dibuja,
 * así que un módulo apagado por el tenant o restringido por rol no reaparece
 * por estar en esta lista.
 */

export interface NavItemConfig {
  /** Igual al `key` del módulo en el backend. */
  key: string;
  label: string;
  icon: LucideIcon;
  /** Solo para quien administra. Se filtra además de lo que haga el servidor. */
  adminOnly?: boolean;
  badge?: "unread";
}

export interface NavGroupConfig {
  label: string;
  items: readonly NavItemConfig[];
}

export const NAV_GROUPS: readonly NavGroupConfig[] = [
  { label: "Hoy", items: [{ key: "home", label: "Qué hay para hoy", icon: Sun }] },
  {
    label: "Atender",
    items: [
      { key: "inbox", label: "Conversaciones", icon: Inbox, badge: "unread" },
      { key: "calendar", label: "Agenda", icon: CalendarDays },
    ],
  },
  {
    label: "Vender",
    items: [
      { key: "pipeline", label: "Pipeline", icon: SquareKanban },
      { key: "contacts", label: "Contactos", icon: Users },
    ],
  },
  {
    label: "Clara",
    items: [
      { key: "agents", label: "Agentes", icon: Bot, adminOnly: true },
      { key: "automations", label: "Automatizaciones", icon: Zap, adminOnly: true },
    ],
  },
  {
    label: "Llegar a más gente",
    items: [
      { key: "segments", label: "Segmentos", icon: Filter, adminOnly: true },
      { key: "campaigns", label: "Campañas", icon: Megaphone, adminOnly: true },
    ],
  },
  {
    label: "Medir y ajustar",
    items: [
      { key: "analytics", label: "Analytics", icon: ChartColumn },
      { key: "integrations", label: "Integraciones", icon: Plug, adminOnly: true },
      { key: "settings", label: "Configuración", icon: Settings, adminOnly: true },
    ],
  },
];

export interface NavItem extends NavItemConfig {
  /** `null` = módulo en construcción: se muestra, pero no navega. */
  href: string | null;
  description: string;
  status: NavigationModule["status"];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/** Cruza el menú de diseño con lo que el servidor habilitó. Los grupos vacíos no se muestran. */
export function buildNav(
  modules: readonly NavigationModule[],
  isAdmin: boolean,
  groups: readonly NavGroupConfig[] = NAV_GROUPS,
): NavGroup[] {
  const byKey = new Map(modules.map((module) => [module.key, module]));
  return groups
    .map((group) => ({
      label: group.label,
      items: group.items.flatMap((item): NavItem[] => {
        const module = byKey.get(item.key);
        if (!module || (item.adminOnly && !isAdmin)) return [];
        const href = module.status === "locked" ? null : module.href;
        return [{ ...item, href, description: module.description, status: module.status }];
      }),
    }))
    .filter((group) => group.items.length > 0);
}

/** `/` solo cuando es exacto; el resto también por prefijo (`/agents/new` es Agentes). */
export function isActive(href: string, pathname: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}
